import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Alert,
    IconButton,
    InputAdornment,
} from '@mui/material';
import {
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
    Download as DownloadIcon,
} from '@mui/icons-material';
import { useDownloadSetupPackageMutation } from '../../api/JsdhNotifikaceService/service/DeviceMutations';
import { useJednotkaQuery } from '../../api/JsdhNotifikaceService/service/AdminMutations';
import type { SetupPackageRequest } from '../../api/JsdhNotifikaceService/service/DeviceService';

export interface RpiDialogDevice {
    id: number;
    hostname?: string | null;
    name?: string | null;
}

interface AddRpiDialogProps {
    open: boolean;
    onClose: () => void;
    /** Pokud je zadané, dialog je v režimu regenerace ZIP pro existující zařízení. */
    device?: RpiDialogDevice | null;
}

const PROFILES = [
    // { value: 'zigbee', label: 'Zigbee (headless IoT)' },
    { value: 'kiosk', label: 'Výjezdový displej' },
    // { value: 'kiosk-zigbee', label: 'Kiosk + Zigbee (kompletní)' },
] as const;

/** Převede název jednotky na hostname prefix: "JSDH Benetice" → "jsdh-benetice-", "Benetice" → "jsdh-benetice-" */
const buildHostnamePrefix = (nazev: string): string => {
    const slug = nazev
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
    const prefix = slug.startsWith('jsdh-') ? slug : `jsdh-${slug}`;
    return prefix + '-';
};

const AddRpiDialog: React.FC<AddRpiDialogProps> = ({ open, onClose, device }) => {
    const downloadMutation = useDownloadSetupPackageMutation();
    const { data: jednotka } = useJednotkaQuery();
    const isEditMode = !!device;

    const hostnamePrefix = jednotka?.nazev ? buildHostnamePrefix(jednotka.nazev) : 'jsdh-';

    const [profile, setProfile] = useState<SetupPackageRequest['profile']>('zigbee');
    const [hostnameSuffix, setHostnameSuffix] = useState('');
    const [deviceName, setDeviceName] = useState('');
    const [wifiSsid, setWifiSsid] = useState('');
    const [wifiPassword, setWifiPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    const hostname = isEditMode ? hostnameSuffix : hostnamePrefix + hostnameSuffix;

    // Předvyplnit formulář při otevření
    useEffect(() => {
        if (open && device) {
            setHostnameSuffix(device.hostname || '');
            setDeviceName(device.name || '');
        }
        if (open && !device) {
            setHostnameSuffix('');
            setDeviceName('');
        }
        // Vždy resetovat WiFi při otevření
        setWifiSsid('');
        setWifiPassword('');
        setShowPassword(false);
    }, [open, device]);

    const suffixValid = isEditMode
        ? /^[a-z0-9-]+$/.test(hostnameSuffix)
        : /^[a-z0-9][a-z0-9-]*$/.test(hostnameSuffix);
    const canSubmit = hostnameSuffix.length > 0 && suffixValid;

    const handleDownload = () => {
        downloadMutation.mutate(
            {
                profile,
                hostname,
                deviceName: deviceName || undefined,
                wifiSsid: wifiSsid || undefined,
                wifiPassword: wifiPassword || undefined,
                deviceId: device?.id,
            },
            {
                onSuccess: () => {
                    onClose();
                },
            }
        );
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {isEditMode ? 'Stáhnout konfiguraci zařízení' : 'Přidat Raspberry Pi'}
            </DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                    <Alert severity="info">
                        {isEditMode
                            ? 'Vygeneruje se nový ZIP balík pro existující zařízení (stejný token). Můžete změnit profil, WiFi a další nastavení.'
                            : 'Vygeneruje se ZIP balík se všemi soubory pro SD kartu. Device token se vytvoří automaticky, Tailscale authkey se stáhne při prvním bootu.'}
                    </Alert>

                    <FormControl fullWidth>
                        <InputLabel>Profil</InputLabel>
                        <Select
                            value={profile}
                            onChange={(e) => setProfile(e.target.value as SetupPackageRequest['profile'])}
                            label="Profil"
                        >
                            {PROFILES.map((p) => (
                                <MenuItem key={p.value} value={p.value}>
                                    {p.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {isEditMode ? (
                        <TextField
                            label="Hostname"
                            value={hostnameSuffix}
                            onChange={(e) => setHostnameSuffix(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                            required
                            error={hostnameSuffix.length > 0 && !suffixValid}
                            helperText={hostnameSuffix.length > 0 && !suffixValid
                                ? 'Pouze malá písmena, číslice a pomlčky'
                                : 'Celý hostname zařízení'}
                            placeholder="jsdh-benetice-displej"
                        />
                    ) : (
                        <Box>
                            <Box sx={{ mb: 0.5, fontFamily: 'monospace', fontSize: '0.95rem', color: 'text.secondary' }}>
                                {hostnamePrefix}
                            </Box>
                            <TextField
                                label="Hostname *"
                                value={hostnameSuffix}
                                onChange={(e) => setHostnameSuffix(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                                fullWidth
                                error={hostnameSuffix.length > 0 && !suffixValid}
                                helperText={hostnameSuffix.length > 0 && !suffixValid
                                    ? 'Pouze malá písmena, číslice a pomlčky'
                                    : `Např. displej, zigbee-garaz`}
                                placeholder="displej"
                            />
                        </Box>
                    )}

                    <TextField
                        label="Název zařízení"
                        value={deviceName}
                        onChange={(e) => setDeviceName(e.target.value)}
                        placeholder="Garáž - IoT"
                        helperText="Volitelné — pro identifikaci v admin panelu"
                    />

                    <TextField
                        label="WiFi SSID"
                        value={wifiSsid}
                        onChange={(e) => setWifiSsid(e.target.value)}
                        helperText="Volitelné — WiFi síť pro RPi"
                    />

                    {wifiSsid && (
                        <TextField
                            label="WiFi heslo"
                            type={showPassword ? 'text' : 'password'}
                            value={wifiPassword}
                            onChange={(e) => setWifiPassword(e.target.value)}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword(!showPassword)}
                                            edge="end"
                                            size="small"
                                        >
                                            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Zrušit</Button>
                <Button
                    onClick={handleDownload}
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    disabled={!canSubmit || downloadMutation.isPending}
                >
                    {downloadMutation.isPending ? 'Generuji...' : 'Stáhnout ZIP'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddRpiDialog;
